import { Box, Typography, Chip, Button } from "@mui/material";
import { useEffect, useState } from "react";
import useEvent from "react-use-event-hook";
import { Table } from "../../components/Table";
import {
  TableColumnInterface,
  TableSort,
} from "../../components/Table/TableTypes";
import { useInvitationsContext } from "../../contexts/invitations/InvitationsContext";
import { useInvitationsActions } from "../../contexts/invitations/UseInvitationsActions";
import {
  DEFAULT_PAGE_SIZE,
  modelDataOrderTransform,
} from "../../core/services/network/NetworkTypes";
import { useSearchHelper } from "../../hooks/useSearchHelper";
import { GRAY_BLACK } from "../../themes/colors";
import Invitation, {
  InvitationDataTableFields,
  invitationDataOrderTransform,
} from "../../core/models/Invitation";
import { useIntl } from "react-intl";
import {
  DateFormat,
  getFormattedDate,
  getStatusColor,
  GlobalStatus,
} from "../../util/utils";
import IntlMessages from "../../components/IntlMessages/IntlMessages";
import { useRequestsContext } from "../../contexts/requests/RequestsContext";
import { useRequestsActions } from "../../contexts/requests/UseRequestsActions";
import { useParams } from "react-router-dom";
import { NewInvitationModal } from "./newInvitationModal.container";
import { PreviewDataDrawer } from "./previewDataDrawer.container";

const Invitations = () => {
  const intl = useIntl();
  const { id } = useParams<{ id: string }>();
  const [openInvitationModal, setOpenInvitationModal] =
    useState<boolean>(false);
  const [showPreviewDataDrawer, setShowPreviewDataDrawer] =
    useState<boolean>(false);

  const { invitationsResponseData, invitationsLoading } =
    useInvitationsContext();
  const { selectedRequest, setSelectedRequest } = useRequestsContext();
  const { dispatchGetInvitations, dispatchCleanInvitationsContext } =
    useInvitationsActions();
  const { setSelectedInvitation } = useInvitationsContext();
  const { dispatchGetRequest } = useRequestsActions();

  //#region Load invitations
  const loadInvitations = (
    page: number,
    ordering?: TableSort<InvitationDataTableFields>,
    search?: string
  ) => {
    dispatchGetInvitations({
      filter: {
        search,
        integration_configuration: selectedRequest?.id || id,
      },
      ordering: modelDataOrderTransform(ordering, invitationDataOrderTransform),
      page,
      size: DEFAULT_PAGE_SIZE,
    });
  };
  //#endregion

  //#region effects
  useEffect(() => {
    return () => dispatchCleanInvitationsContext();
  }, []);
  useEffect(() => {
    if (!selectedRequest && id) {
      dispatchGetRequest(id);
    }
  }, [id, selectedRequest, setSelectedRequest]);
  //#endregion

  //#region Table config
  const columns: Array<TableColumnInterface<InvitationDataTableFields>> = [
    {
      id: "email",
      sortable: true,
      label: "invitation.email",
    },
    {
      id: "organizationUserUid",
      sortable: true,
      label: "invitation.organization.id",
    },
    {
      id: "invitations",
      label: "invitation.type",
      sortable: true,
      handler: (row: Invitation) => {
        const invitationsTypes = row.invitations; // Replace with actual data source
        return (
          <Typography variant="body2" color={GRAY_BLACK}>
            {invitationsTypes && invitationsTypes?.length > 0
              ? invitationsTypes?.map((item, index) =>
                  index
                    ? `, ${intl.formatMessage({
                        id: `invitation.type.${item.type}`,
                      })}`
                    : `${intl.formatMessage({
                        id: `invitation.type.${item.type}`,
                      })}`
                )
              : "-"}
          </Typography>
        );
      },
    },
    {
      id: "status",
      label: "invitation.status",
      sortable: true,
      handler: (row: Invitation) => (
        <Box className="flex align-center">
          <Chip
            color={getStatusColor(row.status)}
            label={
              row.status
                ? intl.formatMessage({
                    id: `invitation.status.${row.status.toLowerCase()}`,
                  })
                : "-"
            }
            className="mr-4"
          />
        </Box>
      ),
    },
    {
      id: "createdOn",
      sortable: true,
      label: "invitation.date",
      handler: (row: Invitation) => {
        return row.createdOn
          ? getFormattedDate(row.createdOn, DateFormat.DayOfMonth)
          : "-";
      },
    },
    {
      id: "preview",
      sortable: false,
      handler: (row: Invitation) => {
        return row.status.toLowerCase() === GlobalStatus.Completed ? (
          <Button
            data-testid="invitations.previewButton"
            variant="text"
            className="pl-[1.25rem]"
            onClick={() => {
              setSelectedInvitation(row);
              setShowPreviewDataDrawer(true);
            }}
          >
            <Typography variant="body2" color="primary" className="underline">
              <IntlMessages id="invitations.list.preview" />
            </Typography>
          </Button>
        ) : (
          ""
        );
      },
    },
  ];
  //#endregion

  //#region Search config
  const onSearch = useEvent(({ page, ordering, search }) => {
    loadInvitations(page, ordering, search);
  });

  const searchHelper = useSearchHelper<InvitationDataTableFields>({
    onSearch,
  });
  //#endregion

  return (
    <>
      <Box
        id="pageWrapper"
        className="w-full flex flex-col flex-1 space-y-[1.875rem]"
      >
        <Box className="h-full">
          <Table
            {...searchHelper.tableProps}
            columns={columns}
            data={invitationsResponseData?.data}
            count={invitationsResponseData?.count}
            loading={invitationsLoading}
            actionTitle="invitation.new"
            onClickAction={() => setOpenInvitationModal(true)}
            onSearch={searchHelper.setSearch}
          />
        </Box>
      </Box>
      {openInvitationModal ? (
        <NewInvitationModal
          open={openInvitationModal}
          onClose={() => setOpenInvitationModal(false)}
        />
      ) : null}
      {showPreviewDataDrawer ? (
        <PreviewDataDrawer
          showDrawer={showPreviewDataDrawer}
          onClose={() => {
            setShowPreviewDataDrawer(false);
          }}
        />
      ) : null}
    </>
  );
};

export { Invitations };
